import {
  Box,
  Button,
  Icon,
  Skeleton,
  Theme,
  Typography,
  useMediaQuery,
} from "@outschool/backpack";
import {
  ListingsPromoBannerButtonFragment,
  ListingsPromoBannerFragment,
  SearchListingsQuery,
} from "@outschool/gql-frontend-generated";
import { faSearch, faTimes } from "@outschool/icons";
import { createPathWithActivity, searchPath } from "@outschool/routes";
import { useProductClicked } from "@outschool/ui-analytics";
import { PhotoListing } from "@outschool/ui-components-website";
import { IconButton } from "@outschool/ui-legacy-component-library";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { mapContentfulFiltersToSearchFilters } from "../SearchFilters";

const MAIN_IMAGE_WIDTH = 344;
const MAIN_IMAGE_HEIGHT = 179;

type ListingsPromoBannerComponentProps = {
  banner: ListingsPromoBannerFragment;
  buttons: ListingsPromoBannerButtonFragment[];
  onMuteBanner(): void;
  onButtonClick(button: ListingsPromoBannerButtonFragment): void;
  buttonSelected?: ListingsPromoBannerButtonFragment | null;
  listingsLoading: boolean;
  listings: SearchListingsQuery["searchListings"]["listings"];
};

export default function ListingsPromoBannerComponent({
  banner,
  buttons,
  onMuteBanner,
  onButtonClick,
  buttonSelected,
  listingsLoading,
  listings,
}: ListingsPromoBannerComponentProps) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const listingWidth = isMobile ? 322 : 234;

  const Link = useLinkComponent();

  const trackProductClicked = useProductClicked();

  const searchFilters = React.useMemo(
    () =>
      mapContentfulFiltersToSearchFilters(buttonSelected?.buttonSearchFilters),
    [buttonSelected?.buttonSearchFilters],
  );

  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        overflow: "hidden",
        position: "relative",
        boxShadow:
          "0px 1px 2px rgba(0, 0, 0, 0.08), 0px 4px 20px rgba(0, 0, 0, 0.08)",
        alignItems: "center",
        textWrap: "pretty",
        flexDirection: "column",

        // Default background
        background: theme.gradients?.primarySecondary,

        // Mobile
        borderRadius: "0px",
        height: "897px",
        margin: "0em",
        padding: "1em .75em",
        gap: "1em",

        // Custom contentful background, if image url exists
        ...(!!banner.backgroundImageMobile?.url
          ? {
              /* Background properties */
              backgroundImage: `url("${banner.backgroundImageMobile?.url}"), ${theme.gradients?.primarySecondary}`,
              backgroundPosition: "left top, center center",
              backgroundRepeat: "no-repeat, repeat-x",
              /* Ensuring the images are fixed width and the center is gradient */
              backgroundSize: "auto 897px, 100% 100%",
            }
          : {}),

        // Desktop
        [theme.breakpoints.up("md")]: {
          borderRadius: "16px",
          height: "422px",
          margin: "1em",
          padding: "1.5em 3em",
          gap: "1em",

          // Custom contentful background, if image url exists
          ...(!!banner.backgroundImageLeft?.url &&
          !!banner.backgroundImageRight?.url
            ? {
                backgroundImage: `url("${banner.backgroundImageLeft?.url}"), url("${banner.backgroundImageRight?.url}"), ${theme.gradients?.primarySecondary}`,
                backgroundPosition: "left top, right top, center center",
                backgroundRepeat: "no-repeat, no-repeat, repeat-x",
                /* Ensuring the images are fixed width and the center is gradient */
                backgroundSize: "auto 499px, auto 499px, 100% 100%",
              }
            : {}),
        },
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          position: "absolute",
          zIndex: 1,
          backgroundColor: "white",
          borderRadius: "50%",

          /* Mobile */
          top: "8px",
          right: "8px",

          [theme.breakpoints.up("md")]: {
            /* Desktop */
            top: "24px",
            right: "60px",
          },
        })}
      >
        <IconButton
          icon={faTimes}
          sx={(theme: Theme) => ({
            fontSize: "24px",
            fill: theme.palette.primary.main,
          })}
          iconProps={{ sx: { fontSize: "1.333em" } }}
          onClick={onMuteBanner}
          aria-label={"Close"}
        />
      </Box>

      {/* Header */}
      <Box sx={{ width: "100%", marginBottom: ".5em" }}>
        <Box sx={{ marginBottom: ".5em" }}>
          <Typography
            variant="h2"
            sx={(theme: Theme) => ({
              fontFamily: !!banner.headerTitleFontDefinition?.fontFamily
                ? banner.headerTitleFontDefinition.fontFamily
                : undefined,
              color: theme.palette.primary.main,
            })}
          >
            {banner.headerTitle}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2">{banner.headerSubtitle}</Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={(theme: Theme) => ({
          display: "grid",

          /* Mobile */
          gridTemplateColumns: "1fr" /* Single column */,
          gridTemplateRows: "repeat(3, auto)" /* 3 rows */,
          gap: "10px" /* Gap between grid items */,
          ".title-and-buttons": {
            gridRow: 1 /* A at the top */,
          },
          ".explore-more-button": {
            gridRow: 3 /* B at the bottom */,
          },
          ".listings": {
            gridRow: 2 /* C spanning from top to bottom */,
          },
          [theme.breakpoints.up("md")]: {
            gridTemplateColumns: "1fr 1fr" /* Two equal sized columns */,
            ".title-and-buttons": {
              gridColumn: 1 /* A on the left */,
              gridRow: "1/2",
            },
            ".explore-more-button": {
              gridColumn: 1 /* B on the left */,
              gridRow: 3,
            },
            ".listings": {
              gridColumn: 2 /* C on the right */,
              gridRow: "1/-1",
            },
          },
        })}
      >
        {/* Buttons Section */}
        <Box
          className="title-and-buttons"
          flex
          sx={(_theme: Theme) => ({
            flexDirection: "column",
          })}
        >
          {/* Buttons Section Buttons */}
          <Box>
            <Box
              flex
              sx={(theme: Theme) => ({
                flexFlow: "wrap",
                [theme.breakpoints.down("md")]: {
                  maxHeight: "120px",
                  overflow: "hidden",
                },
              })}
            >
              {buttons.map(buttonItem => (
                <Button
                  key={buttonItem?.buttonText}
                  size={isMobile ? "small" : "medium"}
                  sx={(theme: Theme) => ({
                    marginRight: ".5em",
                    marginBottom: ".5em",
                    backgroundColor:
                      buttonItem?.buttonText === buttonSelected?.buttonText
                        ? theme.palette.primary.light
                        : undefined,
                  })}
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.currentTarget.blur();
                    onButtonClick(buttonItem);
                  }}
                >
                  {buttonItem?.buttonText}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Explore More Button */}
        <Box className="explore-more-button">
          <Button
            variant={"contained"}
            component={Link}
            to={searchPath(searchFilters)}
            startIcon={<Icon icon={faSearch} />}
          >
            {buttonSelected?.exploreMoreButtonText}
          </Button>
        </Box>

        {/* Listings Section */}
        <Box
          className="listings"
          flex
          sx={(theme: Theme) => ({
            [theme.breakpoints.up("md")]: {
              flexFlow: "wrap",
            },
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
              alignItems: "center",
              maxHeight: "525px",
              overflow: "hidden",
            },
          })}
        >
          {/* Listings */}
          {listingsLoading
            ? [0, 1, 2, 3].map(idx => (
                <Skeleton
                  key={idx}
                  variant="rectangular"
                  height={(listingWidth / MAIN_IMAGE_WIDTH) * MAIN_IMAGE_HEIGHT}
                  width={listingWidth}
                  sx={{
                    borderRadius: "12px",
                    margin: ".25em",
                    flexShrink: 0,
                  }}
                />
              ))
            : listings.map((listing, listingIndex) => (
                <Link
                  to={createPathWithActivity(listing.activity)}
                  trackingName={"listing_promo_banner_listing"}
                  trackingUniqueId={listing.activity.uid}
                  useLinkTracking
                  trackViews={true}
                  onClick={() => trackProductClicked(listing, listingIndex)}
                  key={listingIndex}
                  style={{ margin: ".25em" }}
                  target="_blank"
                >
                  <PhotoListing
                    listing={{
                      ...listing,
                      insertionId: listing.insertionId ?? undefined,
                    }}
                    width={listingWidth}
                  />
                </Link>
              ))}
        </Box>
      </Box>
    </Box>
  );
}
